import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled/macro';
import { theme, Tooltip, DotMenuHorizontalIcon, notEmpty, useToast, DeprecatedErrorWarningIcon } from '@arnold/common';
import { useHistory } from 'react-router-dom';
import { Button, Dropdown } from 'react-bootstrap';
import DropdownToggle from 'react-bootstrap/DropdownToggle';
import { StyledDropdownItem, StyledDropdownMenu } from '../../StyledComponents';
import {
  useCloneTopicWithTopicGroupMutation,
  useDeleteTopicGroupMutation,
  AllowedTopicGroupsQuery,
  useCloneProcessTopicGroupMutation,
  UserSysRole,
  UserQuery,
  TopicGroupTypeCode,
} from '../../../generated/hooks';
import ee from '../../../lib/eventEmitter';

type TopicGroupType = NonNullable<AllowedTopicGroupsQuery['allowedTopicGroups']>[0];

type TopicGroupActionProps = {
  user: UserQuery['user'];
  topicGroup: TopicGroupType;
  showOptions?: boolean;
  setLoading?: (loading: boolean) => void;
  cannotScheduleReason?: string;
  actionButtonText?: string;
  actionButtonOnClick?: (topicGroup: TopicGroupType) => void;
  requireValidForAction?: boolean;
  freeProductVersion: boolean;
  isCustomTopic: boolean;
  topic: TopicGroupType['lastValidTopic'];
};

const Buttons = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const StyledDropdownToggle = styled(DropdownToggle)`
  display: flex;
  top: 6px;
  margin: 0px;
  left: ${theme.spacing.f};
  z-index: 10;
  padding: 0;
  background: transparent;
  border: 0;
  &:after {
    border: none;
  }
`;

const StyledDropdown = styled(Dropdown)`
  & > .btn.dropdown-toggle,
  & > .btn.dropdown-toggle:hover {
    background-color: transparent !important;
  }
`;

export const TopicGroupAction = ({
  topicGroup,
  topicGroup: { name, containsSteps, lastSurvey, lastSurveyGroup, id, typeCode },
  user,
  showOptions,
  setLoading,
  cannotScheduleReason,
  actionButtonText,
  actionButtonOnClick,
  requireValidForAction,
  freeProductVersion,
  topic,
  isCustomTopic,
}: TopicGroupActionProps) => {
  const history = useHistory();
  const { t } = useTranslation('topicOverview');
  const { t: surveyT } = useTranslation('surveysOverview');
  const isValid = topic?.isValid || containsSteps;
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const [cloneTopicWithTopicGroup] = useCloneTopicWithTopicGroupMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['allowedTopicGroups'],
  });
  const [cloneProcessTopicGroup] = useCloneProcessTopicGroupMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['allowedTopicGroups'],
  });
  const [deleteTopicGroupMutation] = useDeleteTopicGroupMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['allowedTopicGroups'],
  });

  const { addToast } = useToast();

  const handleClone = async () => {
    setLoading && setLoading(true);
    containsSteps
      ? await cloneProcessTopicGroup({
          variables: {
            id,
          },
          onCompleted: () => {
            addToast(t('copyCreated'));
            setLoading && setLoading(false);
          },
          onError: () => {
            setLoading && setLoading(false);
          },
        })
      : await cloneTopicWithTopicGroup({
          variables: {
            id: topic?.id!,
          },
          onCompleted: () => {
            addToast(t('copyCreated'));
            setLoading && setLoading(false);
          },
          onError: () => {
            setLoading && setLoading(false);
          },
        });
  };

  const handleDeleteTopicGroup = async () => {
    setLoading && setLoading(true);
    await deleteTopicGroupMutation({
      variables: {
        id,
      },
      onCompleted: () => {
        setLoading && setLoading(false);
      },
      onError: () => {
        setLoading && setLoading(false);
        addToast(t('topicCannotBeDeleted'));
      },
    });
  };

  const menuItems = [
    actionButtonOnClick && {
      label: t('detailLink'),
      key: 'detail',
      handler: () =>
        history.push(
          containsSteps ? `/process/${id}` : `/${isCustomTopic ? 'topicEditor' : 'topicEditorPreview'}/${id}`,
        ),
    },
    (topic?.id || containsSteps) && {
      label: t('copy'),
      key: 'copy',
      handler: () => handleClone(),
    },
    !isCustomTopic &&
      user.systemRole === UserSysRole.SysAdmin && {
        label: t('editOriginalTopic'),
        key: 'editOriginalTopic',
        handler: () => history.push(`/topicEditor/${id}`),
      },
    isCustomTopic && {
      label: t('delete'),
      key: 'delete',
      handler: () => {
        const isProcessSurvey =
          containsSteps && typeCode !== TopicGroupTypeCode.Onboarding && typeCode !== TopicGroupTypeCode.Exit;
        return isProcessSurvey
          ? ee.emitDeleteProcessTopicGroupModal(name, handleDeleteTopicGroup)
          : ee.emitDeleteTopicGroupModal(name, handleDeleteTopicGroup);
      },
      disabled: !!lastSurvey || !!lastSurveyGroup,
      tooltipTitle: lastSurvey || lastSurveyGroup ? t('topicCannotBeDeleted') : undefined,
    },
  ].filter(notEmpty);

  const MenuItemsDropdown = () => (
    <StyledDropdown
      show={isDropdownOpen}
      onToggle={() => setIsDropdownOpen(!isDropdownOpen)}
      drop="down"
      className="ml-6"
    >
      <StyledDropdownToggle color="secondary" variant={'link'} outline={false} data-cy="btn-options">
        <DotMenuHorizontalIcon />
      </StyledDropdownToggle>
      <StyledDropdownMenu data-cy="options-menu">
        {menuItems.map((item) => {
          const dropdownItem = (
            <StyledDropdownItem
              key={item.key}
              onClick={item.handler}
              data-cy={`topic-group-menu-${item.key}`}
              disabled={item.disabled}
            >
              {t(item.label)}
            </StyledDropdownItem>
          );
          return item.tooltipTitle ? (
            <Tooltip title={item.tooltipTitle} key={item.label}>
              {dropdownItem}
            </Tooltip>
          ) : (
            dropdownItem
          );
        })}
      </StyledDropdownMenu>
    </StyledDropdown>
  );

  return (
    <Buttons className="mt-3">
      {!isValid && (
        <Tooltip title={t('topicCannotBeScheduled')}>
          <DeprecatedErrorWarningIcon />
        </Tooltip>
      )}
      {!!actionButtonOnClick && isCustomTopic && freeProductVersion && (
        <Tooltip title={surveyT('freeOrganizationsCannotPlanCustomTopics')}>
          <DeprecatedErrorWarningIcon data-cy="free-plan-schedule-tooltip" />
        </Tooltip>
      )}
      {cannotScheduleReason && (
        <Tooltip title={cannotScheduleReason}>
          <DeprecatedErrorWarningIcon />
        </Tooltip>
      )}
      {showOptions && <MenuItemsDropdown />}
      <Button
        variant="primary"
        className="ml-6 mb-0 mt-0 text-nowrap"
        disabled={(requireValidForAction && !isValid) || (!!actionButtonOnClick && isCustomTopic && freeProductVersion)}
        onClick={() => {
          if (actionButtonOnClick) {
            actionButtonOnClick(topicGroup);
          } else {
            history.push(
              containsSteps ? `/process/${id}` : `/${isCustomTopic ? 'topicEditor' : 'topicEditorPreview'}/${id}`,
            );
          }
        }}
        data-cy={`admin-topic-detail-link-${id}`}
        data-icom={'btn-conversation-subject-card-detail'}
      >
        {actionButtonText || t('detailLink')}
      </Button>
    </Buttons>
  );
};
